// Bulk shipping-label upload (Orders page ⋯ menu → Upload shipping labels).
//
// The operator picks a folder's worth of label files at once. Each file is
// matched to an order BY ITS FILENAME: the stem, tried whole and as each
// underscore-separated part — Amazon labels arrive as
// "1_407-1591342-5410759.pdf" (parcel index, then the order; one file per
// parcel). The BE resolves those keys (POST /orders/attachments/
// resolve), the operator reviews the match table, and the files that are
// ticked are then uploaded one by one through the SAME endpoint the
// Attachments tab uses (POST /orders/:order_id/attachments, kind
// shipping_label) — so the per-order access guard, type allowlist and
// 10 MB cap apply exactly as for a single upload.
//
// Rows that cannot be uploaded (no order, over size, wrong type) stay in the
// table so the operator sees what was skipped and why. An order that already
// carries a shipping label is unticked by default — the dispatch desk prints
// the NEWEST shipping_label attachment, so a second upload supersedes the
// first; ticking it is a deliberate replace.
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle, Loader2, Tag, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import { SHIPPING_LABEL_KIND } from "./order-attachments";

export const MAX_LABEL_BYTES = 10 * 1024 * 1024;
const LABEL_EXTENSIONS = new Set(["pdf", "png", "jpg", "jpeg"]);
const UPLOAD_CONCURRENCY = 3;

// Mirrors orderKeyOfFilename in the BE attachments controller (the longest
// underscore-separated part of the stem): the FE computes it only for the
// local "nothing to look up" check and as the fallback display — the BE
// tries every part and its answer is what counts.
export const orderKeyOfFilename = (filename: string): string => {
    const base = filename.split(/[\\/]/).pop() ?? "";
    const stem = base.replace(/\.[A-Za-z0-9]+$/, "").trim();
    const parts = stem.split("_").map(p => p.trim()).filter(Boolean).sort((a, b) => b.length - a.length);
    return parts[0] ?? "";
};

const extOf = (filename: string): string => {
    const m = /\.([A-Za-z0-9]+)$/.exec(filename);
    return m ? m[1].toLowerCase() : "";
};

export interface ResolveCandidate {
    order_id: string;
    order_number: string;
    warehouse: string;
    status: string;
    sales_channel: string;
    open: boolean;
    has_shipping_label: boolean;
}

interface ResolveResult {
    name: string;
    key: string;
    candidates: ResolveCandidate[];
}

export type RowStatus =
    | "ready"
    | "has_label"
    | "not_found"
    | "ambiguous"
    | "invalid_type"
    | "too_large"
    | "uploading"
    | "done"
    | "failed";

export interface LabelRow {
    id: number;
    file: File;
    key: string;
    status: RowStatus;
    candidates: ResolveCandidate[];
    orderId: string | null;
    include: boolean;
    error: string | null;
}

// Pick the one order a filename means, or null when the operator must choose.
// One candidate → it. Several → the single OPEN one if there is exactly one
// (a Jobalots order split across warehouses has one open copy per warehouse
// still to ship; a BargainFox consolidation copy is closed). Otherwise ask.
export const autoPick = (candidates: ResolveCandidate[]): ResolveCandidate | null => {
    if (candidates.length === 1) return candidates[0];
    const open = candidates.filter(c => c.open);
    return open.length === 1 ? open[0] : null;
};

const UPLOADABLE: ReadonlySet<RowStatus> = new Set<RowStatus>(["ready", "has_label", "ambiguous"]);

const canInclude = (row: LabelRow): boolean => UPLOADABLE.has(row.status) && row.orderId !== null;

type Phase = "resolving" | "review" | "uploading" | "done";

const BulkLabelUploadModal = ({
    files,
    onClose,
}: {
    files: File[];
    onClose: () => void;
}) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>("resolving");
    const [rows, setRows] = useState<LabelRow[]>([]);
    const [resolveFailed, setResolveFailed] = useState(false);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    // Resolve once, on open. Files that fail the local checks never reach
    // the BE; the rest go up as one filename list.
    useEffect(() => {
        let alive = true;
        const base: LabelRow[] = files.map((file, i) => {
            const key = orderKeyOfFilename(file.name);
            let status: RowStatus = "ready";
            if (!LABEL_EXTENSIONS.has(extOf(file.name))) status = "invalid_type";
            else if (file.size > MAX_LABEL_BYTES) status = "too_large";
            else if (!key) status = "not_found";
            return { id: i, file, key, status, candidates: [], orderId: null, include: false, error: null };
        });
        const askable = base.filter(r => r.status === "ready");
        if (askable.length === 0) {
            setRows(base);
            setPhase("review");
            return () => { alive = false; };
        }
        (async () => {
            try {
                const r = await apiFetch(`/orders/attachments/resolve`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ names: askable.map(x => x.file.name) }),
                });
                if (!alive) return;
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data: { results: ResolveResult[] } = await r.json();
                if (!alive) return;
                const byName = new Map<string, ResolveResult>();
                for (const res of data.results ?? []) byName.set(res.name, res);
                setRows(base.map(baseRow => {
                    let row = baseRow;
                    if (row.status !== "ready") return row;
                    const res = byName.get(row.file.name);
                    const candidates = res?.candidates ?? [];
                    if (res?.key) row = { ...row, key: res.key };
                    if (candidates.length === 0) return { ...row, candidates, status: "not_found" };
                    const pick = autoPick(candidates);
                    if (!pick) return { ...row, candidates, status: "ambiguous", orderId: null, include: false };
                    if (pick.has_shipping_label) {
                        return { ...row, candidates, status: "has_label", orderId: pick.order_id, include: false };
                    }
                    return { ...row, candidates, status: "ready", orderId: pick.order_id, include: true };
                }));
                setPhase("review");
            } catch {
                if (!alive) return;
                setRows(base);
                setResolveFailed(true);
                setPhase("review");
                toast.error(t("bulk_labels_resolve_failed"), { toastId: "bulk-labels-resolve-err" });
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const counts = useMemo(() => {
        const c = { ready: 0, has_label: 0, unmatched: 0, selected: 0, done: 0, failed: 0 };
        for (const r of rows) {
            if (r.status === "ready") c.ready += 1;
            else if (r.status === "has_label") c.has_label += 1;
            else if (r.status === "not_found" || r.status === "ambiguous" || r.status === "invalid_type" || r.status === "too_large") c.unmatched += 1;
            else if (r.status === "done") c.done += 1;
            else if (r.status === "failed") c.failed += 1;
            if (r.include && canInclude(r)) c.selected += 1;
        }
        return c;
    }, [rows]);

    const selectable = rows.filter(canInclude);
    const allSelected = selectable.length > 0 && selectable.every(r => r.include);

    const setInclude = (id: number, include: boolean) =>
        setRows(prev => prev.map(r => (r.id === id ? { ...r, include } : r)));

    const toggleAll = (include: boolean) =>
        setRows(prev => prev.map(r => (canInclude(r) ? { ...r, include } : r)));

    const chooseCandidate = (id: number, orderId: string) =>
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            if (!orderId) return { ...r, orderId: null, include: false };
            const pick = r.candidates.find(c => c.order_id === orderId);
            return { ...r, orderId, include: pick ? !pick.has_shipping_label : false };
        }));

    const candidateOf = (row: LabelRow): ResolveCandidate | null =>
        row.candidates.find(c => c.order_id === row.orderId) ?? null;

    const uploadOne = async (row: LabelRow): Promise<{ ok: boolean; error: string | null }> => {
        try {
            const form = new FormData();
            form.append("file", row.file);
            form.append("kind", SHIPPING_LABEL_KIND);
            const r = await apiFetch(`/orders/${encodeURIComponent(row.orderId as string)}/attachments`, {
                method: "POST",
                body: form,
            });
            if (r.ok) return { ok: true, error: null };
            const body = await r.json().catch(() => ({} as { message?: string }));
            return { ok: false, error: body?.message || `HTTP ${r.status}` };
        } catch {
            return { ok: false, error: t("server_error") };
        }
    };

    const startUpload = async () => {
        const queue = rows.filter(r => r.include && canInclude(r));
        if (queue.length === 0) return;
        setPhase("uploading");
        const ids = new Set(queue.map(r => r.id));
        setRows(prev => prev.map(r => (ids.has(r.id) ? { ...r, status: "uploading", error: null } : r)));
        // A small pool: the BE stores each file in its own INSERT, so three
        // in flight is plenty and keeps a 100-file batch to a minute or so
        // without hammering the session.
        let next = 0;
        let done = 0;
        let failed = 0;
        const worker = async () => {
            while (next < queue.length) {
                const row = queue[next++];
                const result = await uploadOne(row);
                if (!mounted.current) return;
                if (result.ok) done += 1; else failed += 1;
                setRows(prev => prev.map(r => (r.id === row.id
                    ? { ...r, status: result.ok ? "done" : "failed", error: result.error, include: false }
                    : r)));
            }
        };
        await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker));
        if (!mounted.current) return;
        setPhase("done");
        if (failed === 0) {
            toast.success(t("bulk_labels_toast_done", { n: String(done) }), { toastId: "bulk-labels-done" });
        } else {
            toast.error(t("bulk_labels_toast_partial", { done: String(done), failed: String(failed) }), { toastId: "bulk-labels-partial" });
        }
    };

    const busy = phase === "resolving" || phase === "uploading";

    const statusCell = (row: LabelRow) => {
        switch (row.status) {
            case "ready": return <span className="bulk-labels-status bulk-labels-status--ok">{t("bulk_labels_status_ready")}</span>;
            case "has_label": return (
                <span className="bulk-labels-status bulk-labels-status--warn">
                    <Tag size={12} />{t("bulk_labels_status_has_label")}
                </span>
            );
            case "not_found": return <span className="bulk-labels-status bulk-labels-status--bad">{t("bulk_labels_status_not_found")}</span>;
            case "ambiguous": return <span className="bulk-labels-status bulk-labels-status--warn">{t("bulk_labels_status_ambiguous")}</span>;
            case "invalid_type": return <span className="bulk-labels-status bulk-labels-status--bad">{t("bulk_labels_status_invalid_type")}</span>;
            case "too_large": return <span className="bulk-labels-status bulk-labels-status--bad">{t("bulk_labels_status_too_large")}</span>;
            case "uploading": return <span className="bulk-labels-status"><Loader2 size={12} className="spin" />{t("bulk_labels_status_uploading")}</span>;
            case "done": return <span className="bulk-labels-status bulk-labels-status--ok"><CheckCircle size={12} />{t("bulk_labels_status_done")}</span>;
            case "failed": return (
                <span className="bulk-labels-status bulk-labels-status--bad" title={row.error ?? undefined}>
                    <AlertCircle size={12} />{t("bulk_labels_status_failed")}{row.error ? ` — ${row.error}` : ""}
                </span>
            );
            default: return null;
        }
    };

    const orderCell = (row: LabelRow) => {
        if (row.status === "ambiguous" || (row.candidates.length > 1 && phase === "review")) {
            return (
                <select
                    className="bulk-labels-select"
                    value={row.orderId ?? ""}
                    disabled={busy}
                    aria-label={t("bulk_labels_choose_order")}
                    onChange={e => chooseCandidate(row.id, e.target.value)}
                >
                    <option value="">{t("bulk_labels_choose_order")}</option>
                    {row.candidates.map(c => (
                        <option key={c.order_id} value={c.order_id}>
                            {c.order_number} · {c.warehouse} · {t(`status_${c.status}`)}
                            {c.has_shipping_label ? ` · ${t("attachments_shipping_label_badge")}` : ""}
                        </option>
                    ))}
                </select>
            );
        }
        const c = candidateOf(row);
        if (!c) return <span className="bulk-labels-muted">{row.key || "—"}</span>;
        return (
            <span className="bulk-labels-order">
                <strong>{c.order_number}</strong>
                <span className="bulk-labels-muted"> · {c.warehouse} · {t(`status_${c.status}`)}</span>
            </span>
        );
    };

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onClose}>
            <div className="modal-card bulk-labels-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("bulk_labels_title")}</h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <p className="bulk-labels-intro">{t("bulk_labels_intro")}</p>

                    {phase === "resolving" ? (
                        <div className="bulk-labels-resolving">
                            <Loader2 size={16} className="spin" /> {t("bulk_labels_resolving", { n: String(files.length) })}
                        </div>
                    ) : (
                        <>
                            <p className="bulk-labels-summary" data-testid="bulk-labels-summary">
                                {phase === "done"
                                    ? t("bulk_labels_done_summary", { done: String(counts.done), failed: String(counts.failed) })
                                    : t("bulk_labels_summary", {
                                        ready: String(counts.ready),
                                        has_label: String(counts.has_label),
                                        unmatched: String(counts.unmatched),
                                    })}
                                {resolveFailed && <span className="bulk-labels-status bulk-labels-status--bad"> {t("bulk_labels_resolve_failed")}</span>}
                            </p>
                            <div className="bulk-labels-table-wrap">
                                <table className="bulk-labels-table">
                                    <thead>
                                        <tr>
                                            <th className="bulk-labels-check">
                                                <input
                                                    type="checkbox"
                                                    aria-label={t("bulk_labels_col_include")}
                                                    checked={allSelected}
                                                    disabled={busy || phase === "done" || selectable.length === 0}
                                                    onChange={e => toggleAll(e.target.checked)}
                                                />
                                            </th>
                                            <th>{t("bulk_labels_col_file")}</th>
                                            <th>{t("bulk_labels_col_order")}</th>
                                            <th>{t("bulk_labels_col_status")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(row => (
                                            <tr key={row.id} className={`bulk-labels-row bulk-labels-row--${row.status}`}>
                                                <td className="bulk-labels-check">
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`${t("bulk_labels_col_include")} ${row.file.name}`}
                                                        checked={row.include && canInclude(row)}
                                                        disabled={busy || phase === "done" || !canInclude(row)}
                                                        onChange={e => setInclude(row.id, e.target.checked)}
                                                    />
                                                </td>
                                                <td className="bulk-labels-file" title={row.file.name}>{row.file.name}</td>
                                                <td>{orderCell(row)}</td>
                                                <td>{statusCell(row)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 18 }}>
                        <button className="btn btn-ghost dispatch-modal-btn" onClick={onClose} disabled={busy}>
                            {phase === "done" ? t("close") : t("cancel")}
                        </button>
                        {phase !== "done" && (
                            <button
                                className="btn btn-success dispatch-modal-btn"
                                onClick={() => void startUpload()}
                                disabled={busy || counts.selected === 0}
                            >
                                {phase === "uploading" ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
                                {phase === "uploading"
                                    ? t("submitting")
                                    : t("bulk_labels_upload_n", { n: String(counts.selected) })}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkLabelUploadModal;
